import React, { useEffect, useRef, useState } from "react";
import { UploadCloud, Trash2, Image as ImageIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export default function LogoUploader() {
  const [logo, setLogo] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/branding").then((r) => { setLogo(r.data.logo || null); setName(r.data.name || ""); }).catch(() => {});
  }, []);

  const pick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 1_200_000) { toast.error("Logo must be under 1.2MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      setBusy(true);
      try {
        const { data } = await api.post("/branding/logo", { logo: reader.result });
        setLogo(data.logo);
        toast.success("Logo saved — it will appear on shared reports");
      } catch (err) {
        toast.error(err.response?.data?.detail || "Could not save logo");
      } finally { setBusy(false); }
    };
    reader.readAsDataURL(file);
  };

  const remove = async () => {
    setBusy(true);
    try { await api.delete("/branding/logo"); setLogo(null); toast("Logo removed"); }
    finally { setBusy(false); }
  };

  return (
    <section>
      <div className="card p-6" data-testid="logo-uploader">
        <div className="flex flex-col sm:flex-row sm:items-center gap-6">
          <div className="flex items-center gap-4 flex-1">
            <div className="grid place-items-center w-20 h-20 rounded-2xl border shrink-0 overflow-hidden" style={{ borderColor: "#E7E8E4", background: "#F6F8F1" }}>
              {logo ? <img src={logo} alt="Agency logo" className="w-full h-full object-contain p-2" /> : <ImageIcon size={26} className="text-ink-3" />}
            </div>
            <div>
              <div className="label-caps">White-label branding</div>
              <h3 className="font-bold tracking-tight text-ink">Agency logo</h3>
              <p className="text-sm text-ink-2 mt-1 max-w-md">Upload your logo to replace the initials mark on shared performance reports. PNG or SVG on a transparent background works best.</p>
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="logo-input" />
            <button onClick={pick} disabled={busy} className="btn-primary focus-ring" data-testid="logo-upload-btn">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} strokeWidth={2} />} {logo ? "Replace" : "Upload logo"}
            </button>
            {logo && <button onClick={remove} disabled={busy} className="btn-secondary focus-ring" data-testid="logo-remove-btn"><Trash2 size={15} strokeWidth={1.75} /></button>}
          </div>
        </div>
      </div>
    </section>
  );
}
